/**
 * Collapsing the application settings with one workspace's overrides (§4.1).
 *
 * This lives in `shared` because both sides need the same answer: the Supervisor
 * acts on it, and the viewer shows what a new session in this workspace will
 * actually start with. Two copies of the rule would drift.
 *
 * Values are validated host-side (`normalizeWorkspaceConfig`); this only layers.
 */
import type { MemberAgents, MemberEgress } from '../org/policy';
import type { MemberExtensions } from '../org/extension-library';
import type { AppSettings, EffectiveSettings, OverridableSetting, WorkspaceConfig } from './types';
import type { WorkspaceTarget } from './targets';
import { NO_MEMBER_TARGETS, type MemberTargets } from '../org/targets-policy';

/**
 * A bare hostname; no scheme, path, port or wildcard — one rule, one host
 * (§5.1, §7.1). And how many of them a list may hold.
 *
 * Shared because the host **discards** anything that does not match
 * (`normalizeWorkspaceConfig`), and the three fields that write these lists only
 * trimmed and lower-cased: `https://api.example.com/v1` and `*.example.com`
 * looked accepted, appeared in the list, and were gone on the next read with
 * nothing said. The host still filters — this is what lets a field refuse an
 * entry in a sentence instead.
 */
export const EGRESS_DOMAIN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
export const MAX_EGRESS_DOMAINS = 64;

/**
 * Whether a blocklist an organization wrote covers a host: the name itself, or
 * any name under it (`org/policy.ts`). Here rather than there so the layering
 * below needs no value from the policy module, which reads the rule above.
 */
export function blockedBy(blocked: readonly string[], host: string): boolean {
  const name = host.toLowerCase();
  return blocked.some((entry) => name === entry || name.endsWith(`.${entry}`));
}

/**
 * The resolvers a sandbox asks, and what a valid one looks like (§7.1).
 *
 * Shared for the same reason the two above are: the host discards anything that
 * does not match, so a field that only trimmed would accept in appearance and
 * forget on the next read.
 *
 * An address, never a name — a resolver named by hostname would have to be
 * resolved by a resolver. IPv4 only: a machine with no IPv6 route answers
 * `ENETUNREACH` for a v6 address, and a resolver that fails on half the networks
 * a laptop sees is worse than one fewer resolver.
 */
export const NAMESERVER = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/** Enough for a local pair plus a public pair; past that it is a list nobody reads. */
export const MAX_NAMESERVERS = 6;

/**
 * What a sandbox resolves with unless this machine says otherwise: Cloudflare,
 * then Google. Two operators, so one provider's outage is not every session's.
 *
 * They are **pinned rather than inherited** because the runtime hands a guest
 * the host's resolvers as they were when the VM's process started and never
 * re-reads them. A laptop carried between networks then leaves a long-running
 * session resolving nothing, while the host — which did pick up the new
 * resolvers — is fine, and so is every new session. Anycast addresses route
 * from wherever the machine is plugged in today, which is the property that
 * removes the failure.
 *
 * A network that only permits its own resolvers is the opposite case, and it is
 * why this is a setting: put the local ones here and they are what a guest asks.
 */
export const DEFAULT_NAMESERVERS = ['1.1.1.1', '8.8.8.8'];

/**
 * The organizations' layer over a workspace (§5.25) — what they **enforce**,
 * and nothing else. It lies over every workspace of a member's, a copy of a
 * project or their own: an organization that locks a setting locks it on the
 * person's machine, not on one folder. Where several hold the same key, the
 * desktop has already chosen whose value this carries (`memberHolds`).
 *
 * Its `defaults` are not layered here, and that is the product's shape rather
 * than an omission: an organization's Settings page *is* the application
 * settings of the machines in it, so what it answers is written into each
 * member's own settings when they join and whenever it moves
 * (`cloud-sync.applyOnboarding`). By the time anything is resolved, an
 * unlocked value the organization chose is already the application's — and a
 * member who has since changed it means it, which a layer sitting over them
 * would quietly undo.
 *
 * What it `enforces` is the other half of that sentence, and it wins over
 * everything: the application's value, and the workspace's own. `defaults` is
 * kept on the layer because that is where an enforced key's value is read
 * from — and because the pages name the organization beside a row it holds.
 *
 * `egress` is the network policy of every organization the member belongs to
 * (`org/policy.ts`), and unlike the rest it reaches **every** workspace on the
 * machine, the member's own included: its allowed hosts join the allowlist,
 * its blocked hosts leave every allowlist a person or a workspace wrote and are
 * listed apart (`orgBlocked`), and the mode it holds is the mode.
 */
export interface OrgLayer {
  defaults: Partial<Pick<AppSettings, OverridableSetting>>;
  enforced: readonly string[];
  egress?: MemberEgress | null;
  /**
   * Which organization holds each enforced key, by name — where a member
   * belongs to several, the first to hold a key is the one it answers to
   * (`memberHolds` in the desktop), and a refusal names it.
   */
  holders?: Readonly<Record<string, string>>;
  /** The agents and models a run is held to (`org/policy.ts`), carried to every start point. */
  agents?: MemberAgents | null;
  /** The libraries a session's MCP servers, skills and plugins are held to (`org/extension-library.ts`). */
  extensions?: MemberExtensions | null;
  /** What the member's organizations forbid of targets (`org/targets-policy.ts`). */
  targets?: MemberTargets | null;
  /**
   * The targets the workspace's organization shares (`org/shared-targets.ts`),
   * each marked `sharedBy` it — added after the workspace's own, one whose id a
   * workspace target already has left out.
   */
  sharedTargets?: WorkspaceTarget[] | null;
}

/** Whether a setting is held by the organization, so a page shows it locked. */
export function enforcedBy(org: OrgLayer | null | undefined, key: string): boolean {
  return !!org && org.enforced.includes(key) && (org.defaults as Record<string, unknown>)[key] !== undefined;
}

/**
 * Both lists, each already clamped by `privacyTerms`, with a term the second
 * repeats in another case dropped. Not that clamp itself: `primitives.ts`
 * reads this module's patterns at load, so importing it back would be a cycle.
 */
function unionIgnoringCase(first: readonly string[], second: readonly string[]): string[] {
  const seen = new Set(first.map((term) => term.toLowerCase()));
  return [...first, ...second.filter((term) => !seen.has(term.toLowerCase()) && seen.add(term.toLowerCase()))];
}

export function resolveSettings(
  application: AppSettings,
  config: WorkspaceConfig,
  org: OrgLayer | null = null
): EffectiveSettings {
  // The organization's own values reached this machine as settings, not as a
  // layer (see `OrgLayer`), so what is resolved against is the application
  // itself — the member's, including everything their organization set.
  const app: AppSettings = application;
  const resolved: EffectiveSettings = {
    defaultModel: config.defaultModel ?? app.defaultModel,
    defaultEffort: config.defaultEffort ?? app.defaultEffort,
    defaultSourceBranch: config.defaultSourceBranch ?? app.defaultSourceBranch,
    // No `maxWorkingSessions` or `maxLiveSandboxes` here: the two ceilings count
    // the whole application (§11.4), so they are read from `AppSettings` at the
    // one place that admits work and never layered per workspace.
    archiveRetentionDays: config.archiveRetentionDays ?? app.archiveRetentionDays,

    sandboxBackend: config.sandboxBackend ?? app.sandboxBackend,
    dockerfile: config.dockerfile ?? app.dockerfile,
    containerRuntime: config.containerRuntime ?? app.containerRuntime,
    vmCpu: config.vmCpu ?? app.vmCpu,
    vmMemoryMb: config.vmMemoryMb ?? app.vmMemoryMb,
    vmDiskGb: config.vmDiskGb ?? app.vmDiskGb,
    dockerDiskGb: config.dockerDiskGb ?? app.dockerDiskGb,

    autonomous: config.autonomous ?? app.autonomous,
    agentConfigSource: config.agentConfigSource ?? app.agentConfigSource,
    agentUpdate: config.agentUpdate ?? app.agentUpdate,
    idleHintMs: config.idleHintMs ?? app.idleHintMs,

    commitCoAuthoredBy: config.commitCoAuthoredBy ?? app.commitCoAuthoredBy,
    privacyMask: config.privacyMask ?? app.privacyMask,
    autoSync: config.autoSync ?? app.autoSync,
    autoLand: config.autoLand ?? app.autoLand,
    audit: config.audit ?? app.audit,
    gitEnabled: config.gitEnabled ?? app.gitEnabled,
    tunnelsEnabled: config.tunnelsEnabled ?? app.tunnelsEnabled,
    agentPreview: config.agentPreview ?? app.agentPreview,
    agentPreviewLogins: config.agentPreviewLogins ?? app.agentPreviewLogins,
    agentProbes: config.agentProbes ?? app.agentProbes,

    egressMode: config.egressMode ?? app.egressMode,

    notifications: config.notifications ?? app.notifications,

    logRetentionMb: config.logRetentionMb ?? app.logRetentionMb,
    logBufferLines: config.logBufferLines ?? app.logBufferLines,

    // Replaced whole rather than merged, unlike the allowlist below it: a list
    // of tools somebody wrote for a workspace is the answer they gave, and one
    // of three skills that silently became five is not it (§5.26).
    agentExtensions: config.agentExtensions ?? app.agentExtensions,

    // The only field that merges rather than replaces: a workspace widens the
    // allowlist for itself, it never narrows what the app allows (§7.1).
    extraDomains: [...new Set([...app.extraDomains, ...config.extraDomains])],

    // No application layer: a blocklist only ever narrows what this workspace
    // is asked about, so there is nothing to inherit or to widen (§7.1).
    blockedDomains: config.blockedDomains,
    // The organizations' blocklist, kept apart because it is not this
    // workspace's to edit and a refusal has to name who decided it.
    orgBlocked: [],
    orgAgents: null,
    orgExtensions: null,

    // Nor here, and more strongly: a host service belongs to the repositories
    // being worked on, so an app-wide default would be opening a hole in every
    // workspace at once (§5.9).
    hostServices: config.hostServices,

    // A team's branch and commit conventions are the repositories' (§5.4).
    gitFormats: config.gitFormats,

    // How the person likes each agent to behave, which no set of repositories
    // answers for (§5.8) — and the language they read what it writes in.
    agentSettings: app.agentSettings,
    statusLineScript: app.statusLineScript,
    writingLanguages: app.writingLanguages,
    // The machine's, not a workspace's: the copy it bounds runs in this app.
    repoSeedMaxMb: app.repoSeedMaxMb,
    // What this person knows to be personal, whichever workspace it turns up in,
    // and what the workspace adds for its own repositories' clients (§7.5) —
    // merged like the allowlist: a workspace masks more, never less.
    privacyTerms: config.privacyTerms?.length ? unionIgnoringCase(app.privacyTerms, config.privacyTerms) : app.privacyTerms,
    // The workspace's own, with no application layer: a target names its projects and hosts.
    targets: config.targets ?? [],
    orgTargets: NO_MEMBER_TARGETS
  };
  if (!org) return resolved;
  resolved.orgAgents = org.agents ?? null;
  resolved.orgTargets = org.targets ?? NO_MEMBER_TARGETS;
  const shared = (org.sharedTargets ?? []).filter((one) => !resolved.targets.some((own) => own.id === one.id));
  if (shared.length) resolved.targets = [...resolved.targets, ...shared];
  resolved.orgExtensions = org.extensions ?? null;
  for (const key of org.enforced) {
    if (enforcedBy(org, key) && key in resolved) {
      (resolved as Record<string, unknown>)[key] = (org.defaults as Record<string, unknown>)[key];
    }
  }
  const egress = org.egress;
  if (!egress) return resolved;
  // Allowed first, then blocked out of the whole union: a host the
  // organization blocks is taken off whatever list put it there, its own
  // allowlist included, and no layer below can put it back (§7.1).
  const blocked = egress.blocked.map((one) => one.domain);
  resolved.extraDomains = [...new Set([...resolved.extraDomains, ...egress.allowed])].filter(
    (host) => !blockedBy(blocked, host)
  );
  resolved.orgBlocked = egress.blocked;
  if (egress.mode) resolved.egressMode = egress.mode.value;
  return resolved;
}

